import {
  TerminalChildrenModeButtonShape,
  TerminalChildrenModeSettings,
} from "../../types/Admin/TerminalAppearanceTypes";

export type ChildrenModePresetTheme = {
  id: string;
  name: string;
  description: string;
  buttonShape: TerminalChildrenModeButtonShape;
  /**
   * Optional match string for children background media filenames.
   * If provided and the preset is active, the terminal will try to pick a background
   * from `appearance.childrenBackgrounds` whose `fileName` includes this substring.
   */
  backgroundFileNameMatch?: string;
  /**
   * Fallback local background asset (served from CRA `public/`).
   * Used when `appearance.childrenBackgrounds` is empty or no match found.
   */
  localBackgroundUrl?: string;
  terminalBackgroundColor: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  buttonTextColor: string;
  buttonFontFamily: string;
  sectionButtonFontSize: number;
  serviceButtonFontSize: number;
  backgroundFitMode: NonNullable<
    TerminalChildrenModeSettings["backgroundFitMode"]
  >;
};

export const CHILDREN_MODE_PRESET_THEMES: ChildrenModePresetTheme[] = [
  {
    id: "space",
    name: "Космос",
    description: "Космический фон и круглые кнопки",
    buttonShape: "CIRCLE",
    backgroundFileNameMatch: "Космос",
    localBackgroundUrl: "/children-backgrounds/space.png",
    terminalBackgroundColor: "#130A2B",
    headerBackgroundColor: "#4B1A7A",
    headerTextColor: "#FFFFFF",
    buttonTextColor: "#1A1A2E",
    buttonFontFamily: "SFPro",
    sectionButtonFontSize: 26,
    serviceButtonFontSize: 22,
    backgroundFitMode: "cover",
  },
  {
    id: "jungle",
    name: "Джунгли",
    description: "Тропический фон и круглые кнопки",
    buttonShape: "CIRCLE",
    backgroundFileNameMatch: "Джунгли",
    localBackgroundUrl: "/children-backgrounds/jungle.png",
    terminalBackgroundColor: "#0F3A2D",
    headerBackgroundColor: "#2F6E44",
    headerTextColor: "#FFFFFF",
    buttonTextColor: "#1A1A2E",
    buttonFontFamily: "SFPro",
    sectionButtonFontSize: 26,
    serviceButtonFontSize: 22,
    backgroundFitMode: "cover",
  },
  {
    id: "sea",
    name: "Морской мир",
    description: "Подводный фон и круглые кнопки",
    buttonShape: "CIRCLE",
    backgroundFileNameMatch: "Морской",
    localBackgroundUrl: "/children-backgrounds/sea.png",
    terminalBackgroundColor: "#0B1B3A",
    headerBackgroundColor: "#2E5BB0",
    headerTextColor: "#FFFFFF",
    buttonTextColor: "#1A1A2E",
    buttonFontFamily: "SFPro",
    sectionButtonFontSize: 26,
    serviceButtonFontSize: 22,
    backgroundFitMode: "cover",
  },
  {
    id: "fairy-forest",
    name: "Сказочный лес",
    description: "Сказочный фон и кнопки-облачка",
    buttonShape: "CLOUD",
    backgroundFileNameMatch: "Сказочный",
    localBackgroundUrl: "/children-backgrounds/fairy-forest.png",
    terminalBackgroundColor: "#213A7B",
    headerBackgroundColor: "#5B59D6",
    headerTextColor: "#FFFFFF",
    buttonTextColor: "#1A1A2E",
    buttonFontFamily: "SFPro",
    sectionButtonFontSize: 26,
    serviceButtonFontSize: 22,
    backgroundFitMode: "cover",
  },
  {
    id: "hero-city",
    name: "Город героев",
    description: "Городской фон и яркие плитки",
    buttonShape: "RECTANGLE",
    backgroundFileNameMatch: "Город",
    localBackgroundUrl: "/children-backgrounds/hero-city.png",
    terminalBackgroundColor: "#F2C6A0",
    headerBackgroundColor: "#E18A5C",
    headerTextColor: "#FFFFFF",
    buttonTextColor: "#1A1A2E",
    buttonFontFamily: "SFPro",
    sectionButtonFontSize: 26,
    serviceButtonFontSize: 22,
    backgroundFitMode: "cover",
  },
];

export const DEFAULT_CHILDREN_PRESET_THEME_ID =
  CHILDREN_MODE_PRESET_THEMES[0].id;

export const getChildrenModePresetTheme = (themeId?: string | null) =>
  CHILDREN_MODE_PRESET_THEMES.find((theme) => theme.id === themeId) ??
  CHILDREN_MODE_PRESET_THEMES[0];

export type ResolvedChildrenModeVisualSettings = {
  isIndividualTheme: boolean;
  buttonShape: TerminalChildrenModeButtonShape;
  backgroundFitMode: NonNullable<
    TerminalChildrenModeSettings["backgroundFitMode"]
  >;
  backgroundOffsetByHeader: boolean;
  activeBackgroundId: number | null;
  backgroundFileNameMatch?: string;
  localBackgroundUrl?: string;
  terminalBackgroundColor: string;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  buttonTextColor?: string;
  buttonFontFamily?: string;
  sectionButtonFontSize?: number;
  serviceButtonFontSize?: number;
  showStandardModeIcons: boolean;
  hideIcons: boolean;
};

export const resolveChildrenModeVisualSettings = (
  childrenMode?: TerminalChildrenModeSettings | null,
): ResolvedChildrenModeVisualSettings => {
  const decorationType = childrenMode?.decorationType ?? "THEME";
  const presetTheme = getChildrenModePresetTheme(childrenMode?.presetThemeId);
  const isIndividual = decorationType === "INDIVIDUAL";
  const hideIcons = childrenMode?.hideIcons ?? false;

  return {
    isIndividualTheme: isIndividual,
    buttonShape: isIndividual
      ? (childrenMode?.buttonShape ?? "RECTANGLE")
      : presetTheme.buttonShape,
    backgroundFitMode:
      (isIndividual
        ? childrenMode?.backgroundFitMode
        : childrenMode?.backgroundFitMode) ??
      presetTheme.backgroundFitMode ??
      "cover",
    backgroundOffsetByHeader: childrenMode?.backgroundOffsetByHeader ?? false,
    activeBackgroundId: isIndividual
      ? (childrenMode?.activeBackgroundId ?? null)
      : null,
    backgroundFileNameMatch: isIndividual
      ? undefined
      : presetTheme.backgroundFileNameMatch,
    localBackgroundUrl: isIndividual
      ? undefined
      : presetTheme.localBackgroundUrl,
    terminalBackgroundColor: isIndividual
      ? "#FFFFFF"
      : presetTheme.terminalBackgroundColor,
    headerBackgroundColor: isIndividual
      ? undefined
      : presetTheme.headerBackgroundColor,
    headerTextColor: isIndividual ? undefined : presetTheme.headerTextColor,
    buttonTextColor: isIndividual ? undefined : presetTheme.buttonTextColor,
    buttonFontFamily: isIndividual ? undefined : presetTheme.buttonFontFamily,
    sectionButtonFontSize: isIndividual
      ? undefined
      : presetTheme.sectionButtonFontSize,
    serviceButtonFontSize: isIndividual
      ? undefined
      : presetTheme.serviceButtonFontSize,
    showStandardModeIcons: hideIcons
      ? false
      : (childrenMode?.showStandardModeIcons ?? false),
    hideIcons,
  };
};

export const CHILDREN_TILE_COLORS = [
  "#FF6B6B",
  "#4ECDC4",
  "#FFE66D",
  "#95E1D3",
  "#F38181",
  "#AA96DA",
  "#FCBAD3",
  "#A8D8EA",
  "#FF9A8B",
  "#6C5CE7",
];

/** Лёгкие вариации оттенка планеты для соседних карточек. */
export const SPACE_PLANET_HUE_ROTATIONS = [0, 14, -12, 20, -16] as const;

export const getSpacePlanetHueRotate = (index: number) =>
  SPACE_PLANET_HUE_ROTATIONS[index % SPACE_PLANET_HUE_ROTATIONS.length];

export const createChildrenTileColorMap = (
  ticketIds: Array<number | string>,
) => {
  const map = new Map<string, string>();
  ticketIds.forEach((ticketId, index) => {
    // В детском дизайне раскраска должна быть стабильной:
    // используем фиксированный порядок палитры и индекс, без Math.random().
    map.set(
      String(ticketId),
      CHILDREN_TILE_COLORS[index % CHILDREN_TILE_COLORS.length],
    );
  });
  return map;
};

const getRelativeLuminance = (hexColor: string): number => {
  const normalized = hexColor.replace("#", "");
  if (normalized.length !== 6) {
    return 0;
  }

  const channels = [0, 2, 4].map(
    (offset) => parseInt(normalized.slice(offset, offset + 2), 16) / 255,
  );
  const toLinear = (channel: number) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;

  return (
    0.2126 * toLinear(channels[0]) +
    0.7152 * toLinear(channels[1]) +
    0.0722 * toLinear(channels[2])
  );
};

export const getReadableTextColor = (
  backgroundColor: string,
  preferredLightText = "#FFFFFF",
  preferredDarkText = "#1A1A2E",
): string => {
  try {
    return getRelativeLuminance(backgroundColor) > 0.55
      ? preferredDarkText
      : preferredLightText;
  } catch {
    return preferredLightText;
  }
};
